import { ConditionGroup, QueryCondition } from "../../models/filter-group.model";

export function moveItemsInArray<T>(
  array: T[],
  fromIndex: number,
  toIndex: number,
  selectedItems: T[],
) {
  const tempDuplicateArray = [...array];
  if (!selectedItems || selectedItems.length === 0) {
    const item = array.splice(fromIndex, 1)[0];
    array.splice(toIndex, 0, item);
    return;
  }

  const selectedIndices = selectedItems
    .map((item) => array.indexOf(item))
    .filter((i) => i !== -1)
    .sort((a, b) => a - b);

  const itemsToMove = selectedIndices.map((i) => array[i]);

  for (let i = selectedIndices.length - 1; i >= 0; i--) {
    array.splice(selectedIndices[i], 1);
  }

  const removedBeforeTarget = selectedIndices.filter((i) => i < toIndex).length;
  const adjustedIndex =
    tempDuplicateArray.length - 1 === toIndex
      ? toIndex
      : toIndex - removedBeforeTarget;

  array.splice(adjustedIndex, 0, ...itemsToMove);
}

export function moveAcrossGroups(
  groups: ConditionGroup[],
  fromGroup: number,
  toGroup: number,
  fromIndex: number,
  toIndex: number,
  conditionsToBeMoved: QueryCondition[],
) {
  const source = groups[fromGroup].conditions;
  const target = groups[toGroup].conditions;

  // Remove subtree from source
  source.splice(fromIndex, conditionsToBeMoved.length);

  // Insert subtree into target
  target.splice(toIndex, 0, ...conditionsToBeMoved);
}


function isDroppableAtTopPosition(toIndex: number, draggedDepth: number): boolean {
  return !(toIndex === 0 && draggedDepth !== 0);
}

function isDropInsideSameDepthSubtree(
  list: QueryCondition[],
  fromIndex: number,
  toIndex: number,
  draggedDepth: number
): boolean {
  for (let i = 0; i < list.length; i++) {
    if (i === fromIndex) continue;
    if (list[i].depth === draggedDepth) {
      const { start, end } = getSubtreeRange(list, i);
      if (toIndex > start && toIndex <= end) {
        return true;
      }
    }
  }
  return false;
}

function hasValidParentAboveTargetIndex(
  list: QueryCondition[],
  toIndex: number,
  draggedDepth: number
): boolean {
  for (let i = toIndex - 1; i >= 0; i--) {
    const depth = list[i].depth;
    if (depth === draggedDepth - 1) return true;
    if (depth !== undefined && depth < draggedDepth - 1) return false;
  }
  return false;
}

export function isValidDrop(
  list: QueryCondition[],
  fromIndex: number,
  toIndex: number
): { valid: boolean; error?: string } {
  if (fromIndex === toIndex) return { valid: true };

  const movingNode = list[fromIndex];
  if (!movingNode || movingNode.depth === undefined) {
    return { valid: false, error: 'Invalid node.' };
  }

  const draggedDepth = movingNode.depth;

  // Step 1: Cannot drop at top unless depth is 0
  if (!isDroppableAtTopPosition(toIndex, draggedDepth)) {
    return { valid: false, error: 'Only root-level items can be placed at the top.' };
  }

  // Step 2: Prevent dropping into same-depth subtree
  if (isDropInsideSameDepthSubtree(list, fromIndex, toIndex, draggedDepth)) {
    return { valid: false, error: 'Cannot drop inside a subtree of the same level.' };
  }

  // Step 3: Check hierarchy rules
  if (draggedDepth > 0 && !hasValidParentAboveTargetIndex(list, toIndex, draggedDepth)) {
    return { valid: false, error: `Items of depth ${draggedDepth} must have a parent of depth ${draggedDepth - 1}.` };
  }

  return { valid: true };
}


/**
 * Returns the inclusive start and end indices of a node's subtree.
 */
function getSubtreeRange(list: QueryCondition[], index: number) {
  const baseDepth = list[index].depth;
  if (baseDepth === undefined) return { start: -1, end: -1 };
  
  let end = index + 1;
  let depth = list[end]?.depth;
  while (end < list.length && depth !== undefined && depth > baseDepth) {
    end++;
  }

  return { start: index, end: end - 1 };
}


export function isValidTargetPlacement(
  targetList: QueryCondition[],
  sourceList: QueryCondition[],
  toIndex: number,
  fromIndex: number,
): { valid: boolean; error: string | null } {
  let isValid = true;
  const currentDepth = sourceList[fromIndex]?.depth;
  if (sourceList.length == 1) {
    return { valid: false, error: 'Each group must contain at least one top-level condition.' };
  }
  if (currentDepth === undefined) {
    return { valid: false, error: 'Invalid source index.' };
  }
  if (toIndex == 0 && currentDepth !== 0) {
    return { valid: false, error: 'Invalid placement for this hierarchy level.' };
  }
  if (currentDepth === 0) {
    isValid = toIndex === 0 || targetList[toIndex - 1]?.depth === 0;
  }

  // Depth > 0 → must have a parent (depth - 1) above
  const requiredParentDepth = currentDepth - 1;

  for (let i = toIndex - 1; i >= 0; i--) {
    const targetDepth = targetList[i].depth;
    if (targetDepth === requiredParentDepth) {
      isValid = true;
    }
    if (targetDepth !== undefined && targetDepth < requiredParentDepth) {
      isValid = false;
    }
  }

  return isValid
    ? { valid: true, error: null }
    : { valid: false, error: 'Invalid placement for this hierarchy level.' };
}
